import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePackagesApi } from '../apis/packages-apis';
import { useProductsApi } from '../apis/products-apis'; // Import products API
import { Package } from '../types';
import { useToast } from "../components/ui/Toast";
import PackageForm, { PackageFormRef } from './PackageForm';
import { NavigationWarningDialog } from '../components/NavigationWarningDialog';
import { useNavigationWarning } from '../hooks/useNavigationWarning';

const emptyPackage: Package = {
  packageId: '',
  packageName: '',
  packageProductName: '',
  packageDescription: '',
  packageWorkflow: '',
  packageSpecialInstructions: '',
  packageApprovalNotes: '',
  packageApprovalSkills: [],
  packageActiveSkills: [],
  packageHelperAgents: [],
  packageChatType: '',
  packageModel: 'gpt-4o',
  packageTags: [],
  tags: [],
  isCore: false,
  accessControl: {
    viewAccessType: 'Owner Only',
    viewAllowedUsers: [],
    editAccessType: 'Owner Only',
    editAllowedUsers: []
  },
  ownerId: ''
};

export const AddPackagePage: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const formRef = useRef<PackageFormRef>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [existingPackages, setExistingPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [newPackage, setNewPackage] = useState<Package>(emptyPackage);
  const packagesApi = usePackagesApi();
  const productsApi = useProductsApi(); // Add products API
  
  // Check if any fields have been modified from their initial empty state
  const hasUnsavedChanges = !isSaving && Object.entries(newPackage).some(([key, value]) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === 'string') {
      return value !== emptyPackage[key as keyof Package];
    }
    return false;
  });

  const {
    showDialog: showNavigationWarning,
    handleNavigationAttempt,
    handleConfirmNavigation,
    handleCancelNavigation
  } = useNavigationWarning({
    shouldWarn: hasUnsavedChanges,
    onConfirm: () => {
      setNewPackage(emptyPackage);
      setIsSaving(false);
    }
  });

  // Handle browser back button and page refresh/close
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const fetchExistingPackages = async () => {
      try {
        const getAllPackages = true;
        const packages = await packagesApi.getPackages(getAllPackages);
        setExistingPackages(packages);
      } catch (error) {
        console.error('Error fetching packages:', error);
        addToast({
          title: "Error",
          description: "Failed to fetch existing packages",
          variant: "destructive",
          duration: 4000
        });
      } finally {
        setLoading(false);
      }
    };

    fetchExistingPackages();
  }, [addToast]);

  const handleSubmit = async () => {
    const isValid = await formRef.current?.validateForm() ?? false;
    if (!isValid) {
      addToast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive",
        duration: 4000
      });
      return;
    }

    try {
      setIsSaving(true);
      
      // Handle product creation if needed
      if (newPackage.packageProductName) {
        try {
          // This will either find the existing product or create a new one
          await productsApi.findOrCreateProduct(newPackage.packageProductName);
        } catch (productError) {
          console.error('Error handling product:', productError);
          addToast({
            title: "Warning",
            description: "There was an issue with the product, but the package creation will proceed",
            variant: "default",
            duration: 4000
          });
          // Continue with package creation despite product issues
        }
      }
      
      // Create the package
      await packagesApi.createPackage(newPackage);
      
      addToast({
        title: "Success",
        description: "Package " + newPackage.packageName + " has been successfully created",
        variant: "default",
        duration: 4000
      });

      navigate('/marketplace/packages');
    } catch (error) {
      console.error('Error creating package:', error);
      setIsSaving(false);
      
      addToast({
        title: "Error",
        description: "Failed to create package: " + String(error),
        variant: "destructive",
        duration: 8000
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <PackageForm
        ref={formRef}
        package={newPackage}
        onPackageChange={setNewPackage}
        onSubmit={handleSubmit}
        isEditing={false}
        setIsReadOnly={() => {}}
        existingPackages={existingPackages}
        hasUnsavedChanges={hasUnsavedChanges}
        onCancel={() => navigate('/marketplace/packages')}
        isSaving={isSaving}
      />

      <NavigationWarningDialog
        open={showNavigationWarning}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
        title="Discard Changes?"
        description="You have unsaved changes that will be lost if you leave this page. Are you sure you want to discard them?"
      />
    </>
  );
};

export default AddPackagePage;